/**
 * AddByPhotoModal — "Add event by photo" flow from the Fim FAB.
 * Pick/take a photo → AI extracts actionable items → user adds each as a
 * task (default list) and/or an event (prefilled create-event modal).
 * No free-form chat. Rendered as a see-through darkened overlay matching
 * the AI chat / AI suggestions modals.
 */

import { Ionicons } from '@expo/vector-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useChatAttachment } from '@/hooks/use-chat-attachment';
import { useChatEventModal } from '@/hooks/use-chat-event-modal';
import { useResolvedDefaultCalendarId } from '@/hooks/use-resolved-default-calendar-id';
import { ChatTaskCreator } from '@/lib/chat/ChatTaskCreator';
import { PhotoActionService } from '@/lib/chat/PhotoActionService';
import { CalendarEventModal } from '@/components/calendar/event-modal';
import { PhotoActionResultCard } from './PhotoActionResultCard';
import type { PhotoActionItem, PhotoActionsResponse } from '@/types/photo-actions';

interface AddByPhotoModalProps {
  visible: boolean;
  onDismiss: () => void;
}

type Phase = 'pick' | 'processing' | 'results' | 'error';

type GroupedItem = { task?: PhotoActionItem; event?: PhotoActionItem; display: PhotoActionItem };

function groupItems(items: PhotoActionItem[]): GroupedItem[] {
  const byTitle = new Map<string, GroupedItem>();
  for (const item of items) {
    const key = item.title.trim().toLowerCase();
    const existing = byTitle.get(key);
    if (existing) {
      if (item.type === 'task') existing.task = item;
      else existing.event = item;
    } else {
      byTitle.set(key, {
        task: item.type === 'task' ? item : undefined,
        event: item.type === 'event' ? item : undefined,
        display: item,
      });
    }
  }
  return [...byTitle.values()];
}

export function AddByPhotoModal({ visible, onDismiss }: AddByPhotoModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();

  const attachment = useChatAttachment();
  const { status: attachmentStatus, buildSendDataUrl, onSendSucceeded } = attachment;
  const eventModal = useChatEventModal();
  const storedDefaultCalendarId = useResolvedDefaultCalendarId(eventModal.writableCalendars);

  const [phase, setPhase] = useState<Phase>('pick');
  const [results, setResults] = useState<PhotoActionsResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const groupedItems = useMemo(() => groupItems(results?.items ?? []), [results]);

  const resetAll = useCallback(() => {
    setPhase('pick');
    setResults(null);
    setErrorMessage(null);
    attachment.clear().catch(() => {});
  }, [attachment]);

  const handleDismiss = useCallback(() => {
    resetAll();
    onDismiss();
  }, [resetAll, onDismiss]);

  // Auto-extract once the compressed attachment is ready. Gated via phaseRef so
  // the synchronous setPhase below doesn't tear down this effect run — a `phase`
  // dep would fire the cleanup (cancelled = true) before extraction resolves.
  useEffect(() => {
    if (!visible || attachmentStatus !== 'ready' || phaseRef.current !== 'pick') return;
    let cancelled = false;
    setPhase('processing');
    (async () => {
      try {
        const dataUrl = await buildSendDataUrl();
        if (!dataUrl) throw new Error('Could not read the photo.');
        const extracted = await PhotoActionService.extract(dataUrl);
        if (cancelled) return;
        setResults(extracted);
        setPhase('results');
        onSendSucceeded().catch(() => {});
      } catch (err) {
        if (cancelled) return;
        console.error('[AddByPhotoModal] extraction failed:', err);
        setErrorMessage((err as Error).message);
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, attachmentStatus, buildSendDataUrl, onSendSucceeded]);

  const handleAddTask = useCallback(async (group: GroupedItem) => {
    const item = group.task ?? group.display;
    await ChatTaskCreator.createTask(item);
  }, []);

  const handleAddEvent = useCallback((group: GroupedItem) => {
    const item = group.event ?? group.display;
    eventModal.openEventModal(item);
  }, [eventModal]);

  const contentTopInset = insets.top + 56;

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={handleDismiss} statusBarTranslucent>
      <View style={styles.overlay}>
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(150)}
          style={[styles.backdrop, { backgroundColor: palette.overlayScrim }]}
        >
          <Pressable style={StyleSheet.absoluteFill} onPress={handleDismiss} />
        </Animated.View>

        <Animated.View
          entering={FadeIn.duration(200).delay(50)}
          style={[styles.header, { paddingTop: insets.top + Spacing.sm }]}
        >
          <View style={styles.headerSpacer} />
          <Text style={[styles.headerTitle, { fontFamily: getBodyFont() }]}>
            Add by photo
          </Text>
          <TouchableOpacity
            onPress={handleDismiss}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityLabel="Close"
          >
            <Ionicons name="close" size={28} color="#FFFFFF" />
          </TouchableOpacity>
        </Animated.View>

        {phase === 'pick' && (
          <View style={[styles.pickContainer, { paddingTop: contentTopInset }]} pointerEvents="box-none">
            <Text style={[styles.pickHint, { fontFamily: getBodyFont() }]}>
              Snap a flyer, list, or invitation — Fim turns it into a task or event.
            </Text>
            <TouchableOpacity
              style={[styles.sourceButton, { backgroundColor: palette.primary }]}
              onPress={() => attachment.attachFromCamera()}
              activeOpacity={0.8}
            >
              <Ionicons name="camera" size={20} color="#FFFFFF" />
              <Text style={[styles.sourceButtonText, { fontFamily: getBodyFont() }]}>Take photo</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.sourceButton, styles.sourceButtonOutline]}
              onPress={() => attachment.attachFromLibrary()}
              activeOpacity={0.8}
            >
              <Ionicons name="images" size={20} color="#FFFFFF" />
              <Text style={[styles.sourceButtonText, { fontFamily: getBodyFont() }]}>
                Choose from library
              </Text>
            </TouchableOpacity>
            {attachment.status === 'compressing' && (
              <ActivityIndicator size="small" color="#FFFFFF" style={styles.pickSpinner} />
            )}
          </View>
        )}

        {phase === 'processing' && (
          <View style={[styles.centerContainer, { paddingTop: contentTopInset }]} pointerEvents="box-none">
            {attachment.displayDataUrl && (
              <Image source={{ uri: attachment.displayDataUrl }} style={styles.previewImage} resizeMode="cover" />
            )}
            <ActivityIndicator size="large" color="#FFFFFF" />
            <Text style={[styles.processingText, { fontFamily: getBodyFont() }]}>
              Reading your photo…
            </Text>
          </View>
        )}

        {phase === 'results' && (
          <ScrollView
            style={{ marginTop: contentTopInset }}
            contentContainerStyle={[styles.resultsContainer, { paddingBottom: insets.bottom + Spacing.xl }]}
          >
            {groupedItems.length === 0 ? (
              <Text style={[styles.emptyText, { fontFamily: getBodyFont() }]}>
                Couldn’t find anything actionable in that photo. Try another one.
              </Text>
            ) : (
              groupedItems.map((group, index) => (
                <PhotoActionResultCard
                  key={`${group.display.title}-${index}`}
                  item={group.display}
                  onAddTask={() => handleAddTask(group)}
                  onAddEvent={() => handleAddEvent(group)}
                />
              ))
            )}
            <Pressable
              onPress={resetAll}
              style={styles.retakeButton}
              accessibilityRole="button"
              accessibilityLabel="Use a different photo"
            >
              <Text style={[styles.retakeText, { fontFamily: getBodyFont() }]}>
                Use a different photo
              </Text>
            </Pressable>
          </ScrollView>
        )}

        {phase === 'error' && (
          <View style={[styles.centerContainer, { paddingTop: contentTopInset }]} pointerEvents="box-none">
            <Ionicons name="alert-circle-outline" size={40} color="#FF6B6B" />
            <Text style={[styles.errorText, { fontFamily: getBodyFont() }]}>
              {errorMessage ?? 'Something went wrong.'}
            </Text>
            <TouchableOpacity
              style={[styles.sourceButton, styles.retryButton, { backgroundColor: palette.primary }]}
              onPress={resetAll}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Try again"
            >
              <Text style={[styles.sourceButtonText, { fontFamily: getBodyFont() }]}>Try again</Text>
            </TouchableOpacity>
          </View>
        )}

        {eventModal.showEventModal && (
          <CalendarEventModal
            visible={true}
            mode="create"
            defaultTitle={eventModal.eventActionItem?.title}
            defaultDescription={eventModal.eventActionItem?.description}
            preselectedDate={eventModal.eventActionItem?.date ?? null}
            preselectedTime={eventModal.eventActionItem?.time ?? null}
            writableCalendars={eventModal.writableCalendars}
            allCalendars={eventModal.allCalendars}
            defaultCalendarId={storedDefaultCalendarId ?? eventModal.writableCalendars[0]?.id ?? null}
            familyMembers={eventModal.familyMembers}
            calendarMappings={eventModal.calendarMappings}
            onSave={eventModal.handleEventSave}
            onDismiss={eventModal.handleEventDismiss}
            isSubmitting={eventModal.isSubmittingEvent}
          />
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1 },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
    paddingBottom: Spacing.md,
  },
  headerSpacer: { width: 28 },
  headerTitle: { fontSize: 18, fontWeight: '700', color: '#FFFFFF' },
  pickContainer: { flex: 1, justifyContent: 'center', padding: Spacing.xxl, gap: Spacing.md },
  pickHint: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: Spacing.lg,
    color: 'rgba(255, 255, 255, 0.75)',
  },
  pickSpinner: { marginTop: Spacing.md },
  sourceButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
    paddingVertical: Spacing.md + 2,
    borderRadius: BorderRadius.md,
  },
  sourceButtonOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: 'rgba(255, 255, 255, 0.4)',
  },
  sourceButtonText: { color: '#FFFFFF', fontSize: 16, fontWeight: '600' },
  centerContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: Spacing.lg, padding: Spacing.xxl },
  previewImage: { width: 140, height: 140, borderRadius: BorderRadius.md, opacity: 0.85 },
  processingText: { fontSize: 14, color: 'rgba(255, 255, 255, 0.75)' },
  resultsContainer: { padding: Spacing.lg, gap: Spacing.md },
  emptyText: { fontSize: 14, textAlign: 'center', color: 'rgba(255, 255, 255, 0.75)' },
  errorText: {
    fontSize: 15,
    color: 'rgba(255, 255, 255, 0.85)',
    textAlign: 'center',
    lineHeight: 22,
  },
  retryButton: { paddingHorizontal: Spacing.xl, alignSelf: 'center' },
  retakeButton: { alignItems: 'center', paddingVertical: Spacing.md },
  retakeText: { fontSize: 14, fontWeight: '600', color: '#FFFFFF' },
});
